import { css } from "lit";

export const styles = css`
  :host {
    display: block;
    position: relative;
  }

  button {
    width: 24px;
    height: 24px;
    border: none;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background: none;
    color: var(--sds-color-text-default-default);
    transition: all 0.2s ease;
  }

  button:hover {
    color: var(--sds-color-text-default-default);
  }

  /* Hamburger Button Styles */
  .hamburger-container {
    position: relative;
    z-index: 20;
    display: flex;
    width: 2rem;
    height: 2rem;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
  }

  .hamburger-container:focus-visible {
    outline: none;
  }

  .hamburger-line {
    width: 100%;
    background-color: var(--sds-color-text-default-default);
    transition: transform 0.3s ease;
  }

  .hamburger-line:nth-child(1) {
    height: 0.25rem;
  }

  .hamburger-line:nth-child(2) {
    height: 1px;
  }

  .hamburger-line:nth-child(3) {
    height: 0.125rem;
  }

  /* Open state animations */
  button[aria-expanded="true"] .hamburger-line:nth-child(1) {
    transform: translateY(0.625rem) rotate(-45deg);
  }

  button[aria-expanded="true"] .hamburger-line:nth-child(2) {
    width: 0;
  }

  button[aria-expanded="true"] .hamburger-line:nth-child(3) {
    transform: translateY(-0.625rem) rotate(45deg);
  }

  #menu-popover {
    min-width: 272px;
    background: var(--sds-color-background-default-default);
    border: 1px solid var(--sds-color-border-default-default);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgb(0 0 0 / 0.15);
    padding: 0;
    margin: 0;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1000;

    /* Popover API styling */
    &:popover-open {
      /* Visible state */
      opacity: 1;
      transform: scale(1) translateY(0);
    }

    /* Starting style for entry animation */
    @starting-style {
      &:popover-open {
        opacity: 0;
        transform: scale(0.95) translateY(-8px);
      }
    }

    /* Transition with allow-discrete for popover display changes */
    transition:
      opacity 0.2s ease,
      transform 0.2s ease,
      overlay 0.2s ease allow-discrete,
      display 0.2s ease allow-discrete;

    /* Exit animation */
    opacity: 0;
    transform: scale(0.95) translateY(-8px);
  }

  .menu-content {
    display: flex;
    flex-direction: column;
  }

  /* User Profile Section */
  .user-profile-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 2rem;
    border-bottom: 1px solid var(--sds-color-border-default-default);
  }

  .user-name {
    font-family: var(--sds-typography-family-title);
    font-size: 24px;
    font-weight: 600;
    color: var(--sds-color-text-default-default);
    margin-bottom: 1rem;
    line-height: 1.2;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .member-info {
    font-family: var(--sds-typography-family-body);
    font-size: var(--sds-typography-body-size-large);
    font-weight: var(--sds-typography-weight-regular);
    color: var(--sds-color-text-default-tertiary);
    margin-bottom: 1rem;
  }

  /* Menu Section */
  .menu-section {
    padding: 1.5rem 1rem;
    border-bottom: 1px solid var(--sds-color-border-default-default);
  }

  .menu-title {
    font-size: var(--sds-typography-body-size-large);
    font-weight: var(--sds-typography-weight-semibold);
    color: var(--sds-color-text-default-tertiary);
    margin: 0 0 0.75rem;
  }

  .menu-items {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .menu-item {
    display: flex;
    font-weight: var(--sds-typography-weight-semibold);
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0.5rem;
    border-radius: 6px;
    text-decoration: none;
    color: var(--sds-color-text-default-default);
    transition: background-color 0.2s ease;
    cursor: pointer;
  }

  .menu-item:hover {
    background-color: var(--sds-color-background-default-default-hover);
  }

  .menu-item-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    flex-shrink: 0;
  }

  .menu-item-text {
    font-size: var(--sds-typography-body-size-medium);
    color: var(--sds-color-text-default-default);
  }

  /* Language Picker Section */
  .language-picker-section {
    padding: 1.5rem;
  }

  .language-picker-buttons {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .language-buttons {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .language-button {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    border: 2px solid var(--sds-color-border-default-default);
    background: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    overflow: hidden;
    box-sizing: border-box;
  }

  .language-button.active {
    border-color: var(--sds-color-border-neutral-default);
  }

  .language-button:hover {
    background: var(--sds-color-background-default-default-hover);
  }

  .language-flag {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
  }

  .theme-toggles {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .theme-toggle {
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    border: 2px solid var(--sds-color-border-default-default);
    background: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    color: var(--sds-color-text-default-default);
  }

  .theme-toggle.active {
    border-color: var(--sds-color-border-neutral-default);
    background: var(--sds-color-background-default-default);
  }

  .theme-toggle:hover {
    background: var(--sds-color-background-default-default-hover);
  }
`;
